import { useEffect, useState } from 'react';

import { type RemoteAction, type RemoteActionCallback } from '../../common/types';
import {
	useMeta,
	useRemoteActionsData,
	useRemoteActionsStoreActions,
} from '../../services/remote-actions-store';

import { computeShouldPerformAction } from './utils';

type RemoteActionFilter = {
	actionName: string;
};

// This hook is exported for public usage.
// Sweet State actions used here cannot consume any container props as
// the consumer of this hook likely won't be children of the Container.
export const useShouldPerformAction = ({ actionName }: RemoteActionFilter) => {
	const metas = useMeta();
	const remoteActions = useRemoteActionsData();
	const { saveActionPerformedAttempt } = useRemoteActionsStoreActions();
	const [actionToPerform, setActionToPerform] = useState<RemoteAction | void>();
	const [shouldPerformAction, setShouldPerformAction] = useState<boolean>(false);

	useEffect(() => {
		// Check whether or not the action (actionName) should be performed:
		// - when the remote actions available list changes
		// - when the metas for the actions change

		const matchedAction = Object.values(remoteActions).find((action) => {
			if (action.name !== actionName) {
				return false;
			}
			return true;
		});

		if (!matchedAction) {
			return;
		}

		setActionToPerform(matchedAction);
		setShouldPerformAction(computeShouldPerformAction(matchedAction, metas));
	}, [actionName, remoteActions, metas]);

	const performAction = (onSuccess: RemoteActionCallback, onCancel: RemoteActionCallback) => {
		if (!actionToPerform) {
			// just in case someone calls the function and don't check if they should before
			// maybe throw an error?
			return;
		}

		const metaForAnalytics = {
			attempts: metas[actionToPerform.id]?.attemptCounts ?? 0,
			lastAttemptedAt: metas[actionToPerform.id]?.lastPerformAttemptedAt,
		};

		if (shouldPerformAction) {
			// For now, the onSuccess will handle:
			// * create analytics event
			// * fire the event
			onSuccess(actionToPerform, metaForAnalytics);
		} else {
			// For now, the onCancel will handle:
			// * create analytics event
			// * fire the event
			onCancel(actionToPerform, metaForAnalytics);
		}

		saveActionPerformedAttempt(actionToPerform.id);

		// We just attempted to perfom the action, reset.
		setShouldPerformAction(false);
	};

	return { shouldPerformAction, performAction };
};
